import { ProductEditor } from './ProductEditor.js';
import { ObjectUtils } from '../main/ObjectUtils.js';
import { ComponentType, DoorFrontInteraction } from './DoorFrontInteraction.js';
import { DoorCore } from './Components.js';
import { MultiStepDoorFrontEditor } from './MultiStepDoorFrontEditor.js';

/**
 * The DoorFrontEditor is a ViewModel for the part of the sliding door front's user interface which
 * is responsible for accepting edits by the user.
 */
export class DoorFrontEditor extends ProductEditor {
  /**
   * This constructor is async because--if session storage contains a product--it will communicate
   * with the remote designerService to load the product. The async load happens on the last line
   * of the super constructor.
   */
  constructor(config, designerService, productService) {
    super(config, designerService, productService);
    this.componentType = ComponentType;
    this.elements.panelList = document.getElementById(config.UI.El.panelList);

    this.allPanelLayoutsMap = config.allPanelLayoutsMap;
    this.panelListContent = config.panelListContent;
    this.panelListContent.colClasses = this.panelListContent.colClass.split(' ');

    this.addNumberInputEventHandlers(this.elements.width, (changedElement, changedValue) => {
      this.propertyChanged('w', changedValue, changedElement);
    });
    this.addNumberInputEventHandlers(this.elements.height, (changedElement, changedValue) => {
      this.propertyChanged('h', changedValue, changedElement);
    });

    this.addDropdownListEventHandlers(this.elements.trackType, (changedElement, changedValue) => {
      this.propertyChanged('railCount', parseInt(changedValue), this.elements.trackType, this.elements.trackFinish);
    });

    const pnlList = jQuery(this.elements.panelList);
    pnlList.on('hidden.bs.collapse', (event) => {
      this.togglePreviewDrawingFinishLabels(false);
    });
    pnlList.on('shown.bs.collapse', (event) => {
      this.togglePreviewDrawingFinishLabels(true);
    });
  }

  assignEditFieldElements(elementIds) {
    super.assignEditFieldElements(elementIds);
    const byId = id => document.getElementById(id);
    this.elements.finish = byId(elementIds.finish);
    this.elements.frameFinish = byId(elementIds.frameFinish);
    this.elements.trackFinish = byId(elementIds.trackFinish);
    this.elements.panelLayout = byId(elementIds.panelLayout);
    this.elements.width = byId(elementIds.width);
    this.elements.height = byId(elementIds.height);
    this.elements.trackType = byId(elementIds.trackType);
  }

  /**
   * Factory method used by the owner ProductPageViewModel.
   */
  createPopupEditor(editDrawing) {
    const config = this.config.UI.popupEditor;
    if (!config.showEditScopeStep) {
      // In this case we also need to set the following defaults:
      ComponentType.panel.useEditScope = false;
      ComponentType.panel.defaultEditScope = 'panel';
      ComponentType.layout.useEditScope = false;
      ComponentType.layout.defaultEditScope = 'door';
    }
    return new MultiStepDoorFrontEditor(editDrawing, config,
      ComponentType.panel, DoorFrontInteraction, 'DoorFrontPopupEditorVM');
  }

  /** Adds button html elements to the buttons array parameter. */
  registerPopupEditorButtons(buttons) {
    super.registerPopupEditorButtons(buttons);
    buttons.push(
      this.elements.finish,
      this.elements.frameFinish,
      this.elements.trackFinish,
      this.elements.panelLayout
    );
  }

  ///**
  // * Assigns BootStrap a modal popup "show" event handler which rebuilds the popup editor based
  // * on the currently edited component type (based on which button was clicked). Note, the method
  // * first calls the super method to add additional popup event handlers .
  // */
  //addPopupEditorHandlers() {
  //  super.addPopupEditorHandlers();
  //  const popup = jQuery(this.popupEditorConfig.elements.modalPopup);
  //  popup.on('show.bs.modal', async (event) => {
  //    if (this.popupEditorDisplayed) {
  //      const typeId = popup.data('compType');
  //      this.editedComponentType = ComponentType[typeId];
  //      this.rebuildPopupEditor(this.editedComponentType);
  //    }
  //  });
  //}

  rebuildValidValuesMaps(validators) {
    super.rebuildValidValuesMaps();
    this.validValuesMap.panel = validators.finish.values;
    this.validValuesMap.frame = validators.frameFinish.values;
    this.validValuesMap.track = validators.trackFinish.values;
    this.validValuesMap.layout = validators.panelLayout.values;
  }

  createSortedDoorDesigns(validValues, allValuesMap) {
    const sortedValues = Array(validValues.length);
    let index = 0;
    validValues.forEach(id => {
      sortedValues[index] = allValuesMap[id];
      sortedValues[index].id = id;
      sortedValues[index].panelCount = DoorCore.calculatePanelCount(id);
      index++;
    });
    sortedValues.sort((a, b) => a.panelCount - b.panelCount);
    return sortedValues;
  }

  createSortedGalleryItems(componentType) {
    const validValues = this.validValuesMap[componentType.id];
    const allValuesMap = componentType === ComponentType.layout ? this.allPanelLayoutsMap : this.allFinishesMap;
    return componentType === ComponentType.layout
      ? this.createSortedDoorDesigns(validValues, allValuesMap)
      : this.createSortedValues(validValues, allValuesMap);
  }

  displayPanelList() {
    const columns = document.createDocumentFragment();
    let doorNo = 1;
    this.product.doors.forEach(door => {
      const columnDiv = document.createElement('div');
      columnDiv.classList.add(...this.panelListContent.colClasses);

      const para = document.createElement('p');
      para.classList.add('door-title');
      para.innerText = `${this.panelListContent.doorPrefix} ${doorNo}`;
      columnDiv.appendChild(para);

      const ol = document.createElement('ol');
      const panels = door.core.panels;
      panels.forEach(panel => {
        const li = document.createElement('li');
        this.assignValidValue(li, this.allFinishesMap, panel.f);
        ol.appendChild(li);
      });
      columnDiv.appendChild(ol);
      columns.appendChild(columnDiv);
      doorNo++;
    });
    ObjectUtils.clearChildren(this.elements.panelList);
    this.elements.panelList.appendChild(columns);
  }

  doDisplayProduct(product, validators, changedElement, changeLog) {
    super.doDisplayProduct(product, validators, changedElement, changeLog);
    this.assignValidValue(this.elements.finish, this.allFinishesMap, product.f, changeLog, 'Finish', changedElement);
    this.assignValidValue(this.elements.frameFinish, this.allFinishesMap, product.frameF, changeLog, 'Frame finish', changedElement);
    this.assignValidValue(this.elements.trackFinish, this.allFinishesMap, product.topTrack.f, changeLog, 'Track finish', changedElement);
    this.assignValidValue(this.elements.panelLayout, this.allPanelLayoutsMap, product.panelLayout, changeLog, 'Door design', changedElement);

    let validator = validators ? validators.width : null;
    this.displayNumberInput(this.elements.width, product.w, validator, this.fieldsCfg.width,
      changeLog, 'Width', changedElement);

    validator = validators ? validators.height : null;
    this.displayNumberInput(this.elements.height, product.h, validator, this.fieldsCfg.height,
      changeLog, 'Height', changedElement);

    validator = validators ? validators.railCount : null;
    this.displayDropdownList(this.elements.trackType, product.railCount, changeLog, 'Track Type',
      changedElement, this.config.trackTypeDValues);

    this.displayPanelList();
  }
}
